import { useEffect, useRef, useState } from 'react';
import { engine } from '../engine/engine';
import { hasSupportedGamepad, isSupportedGamepad } from '../engine/input';
import type { RuleSnapshot } from '../engine/rules';
import styles from './ControlGuide.module.css';

const FIRST_NUDGE_RECHARGE_MS = 5_000;
const SUBSEQUENT_NUDGE_RECHARGE_MS = 10_000;

const keyboardRows = [
  { action: 'LEFT', keys: ['Shift', 'Z', '←', 'Mouse 1'] },
  { action: 'RIGHT', keys: ['Right Shift', '/', '→', 'Mouse 2'] },
  { action: 'PLUNGE', keys: ['↓', 'Space'] },
  { action: 'NUDGE', keys: ['W', 'A', 'D', '↑', 'Q', 'E'] },
];

const gamepadRows = [
  { action: 'FLIPPERS', keys: ['LB/L1', 'RB/R1'] },
  { action: 'PLUNGE', keys: ['A/×'] },
  { action: 'NUDGE', keys: ['D-Pad', 'Left Stick'] },
  { action: 'PAUSE', keys: ['Menu/Options'] },
  { action: 'VIEW · MUTE', keys: ['X/□', 'Y/△'] },
];

interface NudgeUiState {
  remaining: number;
  max: number;
  progress: number;
  tilted: boolean;
}

function sameMissionState(previous: RuleSnapshot | null, next: RuleSnapshot | null): boolean {
  if (previous === next) return true;
  if (!previous || !next) return false;
  if (
    previous.completedMissions !== next.completedMissions ||
    previous.totalMissions !== next.totalMissions ||
    previous.combo !== next.combo ||
    previous.allComplete !== next.allComplete
  ) return false;

  const previousMission = previous.mission;
  const nextMission = next.mission;
  if (previousMission === nextMission) return true;
  if (!previousMission || !nextMission) return false;
  return (
    previousMission.id === nextMission.id &&
    previousMission.name === nextMission.name &&
    previousMission.description === nextMission.description &&
    previousMission.progress === nextMission.progress &&
    previousMission.target === nextMission.target
  );
}

function readGamepadConnection(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return false;
  return hasSupportedGamepad(navigator.getGamepads());
}

export default function ControlGuide() {
  const [rules, setRules] = useState<RuleSnapshot | null>(null);
  const [gamepadConnected, setGamepadConnected] = useState(false);
  const [nudgeUi, setNudgeUi] = useState<NudgeUiState>({ remaining: 3, max: 3, progress: 0, tilted: false });
  const nudgeRef = useRef(nudgeUi);
  const lastSnapshotAt = useRef<number | null>(null);
  const lastPublishedAt = useRef(0);

  useEffect(() => {
    const syncGamepad = () => setGamepadConnected(readGamepadConnection());
    const onGamepadConnected = (event: GamepadEvent) => {
      // The connection event is authoritative even in browsers where
      // navigator.getGamepads() updates a frame later.
      if (isSupportedGamepad(event.gamepad)) setGamepadConnected(true);
      else syncGamepad();
    };

    syncGamepad();
    window.addEventListener('gamepadconnected', onGamepadConnected);
    window.addEventListener('gamepaddisconnected', syncGamepad);
    return () => {
      window.removeEventListener('gamepadconnected', onGamepadConnected);
      window.removeEventListener('gamepaddisconnected', syncGamepad);
    };
  }, []);

  useEffect(
    () => engine.onSnapshot((snapshot) => {
      const nextRules = snapshot.rules ?? null;
      setRules((previous) => (sameMissionState(previous, nextRules) ? previous : nextRules));

      const now = typeof performance === 'undefined' ? Date.now() : performance.now();
      const previous = nudgeRef.current;
      const max = Math.max(0, Math.floor(engine.table?.board.nudgeCount ?? 3));
      const remaining = Math.max(0, Math.min(max, snapshot.nudgesRemaining));
      const elapsed = lastSnapshotAt.current === null ? 0 : Math.max(0, now - lastSnapshotAt.current);
      lastSnapshotAt.current = now;

      let progress = previous.progress;
      const stockChanged = remaining !== previous.remaining || max !== previous.max;
      if (remaining >= max || remaining !== previous.remaining || snapshot.tilted) {
        progress = 0;
      } else if (max > 0) {
        // Physics restores the first point from zero in five seconds, then
        // restores each later stock point in ten seconds. Advancing from
        // snapshot time keeps this meter in lockstep with the running game
        // instead of wall-clock time while a tab is suspended.
        const rechargeMs = remaining === 0 ? FIRST_NUDGE_RECHARGE_MS : SUBSEQUENT_NUDGE_RECHARGE_MS;
        progress = Math.min(1, progress + elapsed / rechargeMs);
      }

      const next = { remaining, max, progress, tilted: snapshot.tilted };
      nudgeRef.current = next;
      const publish = stockChanged || snapshot.tilted !== previous.tilted || now - lastPublishedAt.current >= 100;
      if (publish) {
        lastPublishedAt.current = now;
        setNudgeUi(next);
      }
    }),
    [],
  );

  const mission = rules?.mission ?? null;
  const hasMissions = Boolean(rules?.totalMissions);
  const recharging = !nudgeUi.tilted && nudgeUi.max > 0 && nudgeUi.remaining < nudgeUi.max;
  const controlRows = gamepadConnected ? gamepadRows : keyboardRows;

  return (
    <aside className={styles.guide} aria-label="Game objective and controls">
      {hasMissions && (
        <section className={styles.mission} aria-live="polite">
          <div className={styles.missionHeading}>
            <strong>{rules?.allComplete ? 'JACKPOT' : 'NEXT SHOT'}</strong>
            <span>{rules?.completedMissions ?? 0}/{rules?.totalMissions ?? 0}</span>
          </div>
          {rules?.allComplete ? (
            <h2>Keep it rolling.</h2>
          ) : mission ? (
            <>
              <h2>{mission.name}</h2>
              <div className={styles.progressRow}>
                <div className={styles.progressTrack} aria-hidden="true">
                  <span style={{ width: `${Math.min(100, (mission.progress / mission.target) * 100)}%` }} />
                </div>
                <b>{mission.progress}/{mission.target}</b>
              </div>
              {(rules?.combo ?? 0) > 1 && <small>COMBO ×{rules?.combo}</small>}
            </>
          ) : null}
        </section>
      )}

      <section className={styles.nudgeMeter} aria-label={`${nudgeUi.remaining} of ${nudgeUi.max} nudges available`}>
        <div className={styles.nudgeHeading}>
            <strong>NUDGE</strong>
          <span className={nudgeUi.tilted ? styles.nudgeTilt : ''}>{nudgeUi.tilted ? 'TILT' : `${nudgeUi.remaining}/${nudgeUi.max}`}</span>
        </div>
        <div className={styles.nudgePips} aria-hidden="true">
          {Array.from({ length: nudgeUi.max }, (_, index) => (
            <i key={index} className={index < nudgeUi.remaining ? styles.nudgeReady : ''} />
          ))}
        </div>
        {recharging && (
          <div className={styles.nudgeRecharge}>
            <div><span style={{ width: `${Math.round(nudgeUi.progress * 100)}%` }} /></div>
          </div>
        )}
      </section>

      <details className={styles.controls}>
        <summary>{gamepadConnected ? 'Gamepad controls' : 'Controls'}</summary>
        {mission?.description && <p>{mission.description}</p>}
        <div className={styles.controlsHeading} aria-live="polite">
          <strong>{gamepadConnected ? 'GAMEPAD' : 'CONTROLS'}</strong>
          {gamepadConnected && <span aria-label="Standard gamepad connected">CONNECTED</span>}
        </div>
        {controlRows.map((row) => (
          <div key={row.action} className={styles.row}>
            <span>{row.action}</span>
            <div>
              {row.keys.map((key) => (
                <kbd key={key}>{key}</kbd>
              ))}
            </div>
          </div>
        ))}
      </details>
    </aside>
  );
}
